import { DeleteButton } from "@components/buttons/delete";
import { RollBackButton } from "@components/buttons/rollback";
import {
  SHAutocomplete,
  SHButton,
  SHChip,
  SHFilterOption,
  SHImage,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { SHTextOverflowTooltip } from "@components/design-systems/sh-text-overflow-tooltip";
import { EditSVG, PlusSVG } from "@components/svgs";
import { DateTimeFormat } from "@constants/format";
import { useAnalysisFeePortfolioSetupItem } from "@hooks/useFeeCalculatorPortfolioSetupItem";
import { useNotification } from "@hooks/useNotification";
import { AnalysisFeePortfolioSetupDTO } from "@models/platform-analysis/entities/steps/fee";
import { useTheme } from "@mui/material";
import { shouldHideSubProductName } from "@pages/platform-analysis-v2/_id/utils";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useToggle, useUnmount } from "react-use";
import { AnalysisFeeCalculatorStepConfigs as Configs } from "../../../../configs";
import { AnalysisFeeCalculatorErrorMessages as ErrorMessages } from "../../../../constants";
import {
  EditPortfolioSetupParams,
  getSetupItemErrorData,
  getSetupItemWarningData,
} from "../utils";
import { AnalysisFeePortfolioWarningIcon } from "../warning-icon";

type AnalysisFeePortfolioSetupItemProps = {
  data: AnalysisFeePortfolioSetupDTO;
  portfolios: SHFilterOption[];
  itemIndex: number;
  isLastItem?: boolean;
  isSectionDisabled?: boolean;
  isReview?: boolean;

  onEditButtonClick: (params: EditPortfolioSetupParams) => void;
  onCreateButtonClick: (setupIndex: number) => void;
  onSelectPortfolio?: (
    portfolioSetup: AnalysisFeePortfolioSetupDTO,
    itemIndex: number,
  ) => void;
};

export const SetupItem = ({
  data,
  portfolios,
  itemIndex,
  isSectionDisabled,
  isReview,
  onEditButtonClick,
  onCreateButtonClick,
  onSelectPortfolio,
}: AnalysisFeePortfolioSetupItemProps) => {
  const { palette } = useTheme();
  const { notify } = useNotification();

  const { name: productName, logo: productLogo } = data.product || {};
  const { excludedFromFeeAnalysis, name, setupType, outdatedAt, isLatest } =
    data;

  const {
    formMethod: { control },
    isLastRemainingSubProduct,
    fieldPaths,
    excludeItemFromFeeAnalysis,
    includeItemInFeeAnalysis,
    resetItemValues,
  } = useAnalysisFeePortfolioSetupItem({
    itemData: data,
    itemIndex,
  });

  const [isArchived, toggleIsArchived] = useToggle(!!excludedFromFeeAnalysis);
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<SHFilterOption | null>(
      !data.excludedFromFeeAnalysis
        ? portfolios?.find((item) => item.value === data.selectedPortfolioId) ??
            null
        : null,
    );

  const subProductName = `${name}${
    !isLatest && outdatedAt
      ? ` (Outdated: ${format(outdatedAt, DateTimeFormat)})`
      : ""
  }`;

  const isDisabled = useMemo(() => data.hasNoInvestmentData, [data]);

  const showEditButton = useMemo(
    () => !isArchived && !isDisabled,
    [isArchived, isDisabled],
  );

  const handleOnDelete = () => {
    toggleIsArchived(true);
    setSelectedPortfolio(null);
    excludeItemFromFeeAnalysis();
  };

  const handleOnRollBack = () => {
    toggleIsArchived(false);
    includeItemInFeeAnalysis();
  };

  const handlePortfolioButtonClick = () => {
    if (selectedPortfolio) {
      onEditButtonClick({
        portfolioId: selectedPortfolio.value,
        setupIndex: itemIndex,
      });

      return;
    }

    if (portfolios.length >= Configs.MaximumPortfolioSelections) {
      notify(ErrorMessages.MaximumPortfolioSelectionsReached, {
        variant: "error",
        close: true,
      });
      return;
    }

    onCreateButtonClick(itemIndex);
  };

  const handleSelectedPortfolioChange = (newValue: SHFilterOption | null) => {
    setSelectedPortfolio(newValue);
    const newPortfolio = {
      ...data,
      selectedPortfolioId: newValue?.value || null,
    };
    onSelectPortfolio?.(newPortfolio, itemIndex);
  };

  const getWarningIcon = () => {
    const warningData = getSetupItemWarningData(data);
    if (!warningData) return null;

    return (
      <AnalysisFeePortfolioWarningIcon
        type={"warning"}
        warningData={warningData}
      />
    );
  };

  const getErrorIcon = () => {
    const errorData = getSetupItemErrorData(data);
    if (!errorData) return null;

    return (
      <AnalysisFeePortfolioWarningIcon type={"error"} warningData={errorData} />
    );
  };

  useEffect(() => {
    if (!data.excludedFromFeeAnalysis) {
      setSelectedPortfolio(
        portfolios?.find((item) => item.value === data.selectedPortfolioId) ??
          null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.selectedPortfolioId]);

  useUnmount(() => {
    resetItemValues();
  });

  return (
    <SHStack height={50} direction={"row"}>
      <SHStack
        width={"38%"}
        direction={"row"}
        gap={2}
        justifyContent={"space-between"}
      >
        <SHStack
          height={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
          position={"relative"}
          sx={{
            "& .PortfolioSetupItemMainBadge": {
              borderColor: `${palette.warning.main} !important`,
            },
          }}
        >
          {data.product?.isProductMain && (
            <SHChip
              className={"PortfolioSetupItemMainBadge"}
              label={isReview ? "Current" : "Main"}
              color={"warning"}
              variant={"outlined"}
              sx={{
                fontSize: "10px",
                fontWeight: 500,
                padding: "2px",
                "&>.MuiChip-label": {
                  padding: "0px 4px",
                  textOverflow: "initial",
                },
                height: "auto",
                maxWidth: "none",
                position: "absolute",
                top: "-1px",
                right: "-10px",
                zIndex: 2,
              }}
            />
          )}
          <SHImage
            src={productLogo ?? ""}
            width={40}
            height={40}
            style={{
              filter:
                isArchived || isDisabled || isSectionDisabled
                  ? "grayscale(100%)"
                  : "none",
            }}
          />
        </SHStack>

        <SHStack
          direction={"column"}
          gap={0}
          alignItems={"start"}
          alignContent={"center"}
          justifyContent={"center"}
          paddingTop={1}
          width={"100%"}
        >
          <SHTextOverflowTooltip
            tooltipContent={productName}
            lineClamp={1}
            breakAnyWhere
            preventGetFitWidth
          >
            <SHTypography
              addPaddingTop={false}
              variant={"body2"}
              fontWeight={"bold"}
              fontSize={14}
              lineHeight={1.2}
              disabled={isArchived || isDisabled || isSectionDisabled}
            >
              {productName}
            </SHTypography>
          </SHTextOverflowTooltip>

          {!shouldHideSubProductName(setupType, productName) && (
            <SHTextOverflowTooltip
              tooltipContent={subProductName}
              lineClamp={1}
              breakAnyWhere
              preventGetFitWidth
            >
              <SHTypography
                addPaddingTop={false}
                variant={"body2"}
                fontSize={14}
                lineHeight={1.2}
                color={palette.text.third}
                disabled={isArchived || isDisabled}
              >
                {subProductName}
              </SHTypography>
            </SHTextOverflowTooltip>
          )}
        </SHStack>

        <SHStack
          direction={"column"}
          alignItems={"center"}
          justifyContent={"center"}
          paddingTop={0.5}
          gap={0.5}
        >
          {getWarningIcon()}
          {getErrorIcon()}
        </SHStack>
      </SHStack>

      <SHStack width={isDisabled ? "60%" : "25%"} direction={"row"}>
        {showEditButton && (
          <SHStack
            width={"100%"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <SHButton
              disabled={isArchived || isSectionDisabled}
              variant={"outlined"}
              size={"medium"}
              sx={{
                width: "80%",
                ":disabled": {
                  svg: { path: { fill: palette.text.disabled } },
                },
                ":active": {
                  svg: { path: { fill: palette.common.white } },
                },
              }}
              startIcon={selectedPortfolio ? <EditSVG /> : <PlusSVG />}
              onClick={handlePortfolioButtonClick}
            >
              Portfolio
            </SHButton>
          </SHStack>
        )}

        {isArchived && (
          <SHStack
            width={"100%"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <SHButton
              disabled={true}
              variant={"outlined"}
              size={"medium"}
              sx={{
                width: "80%",
              }}
            >
              Removed
            </SHButton>
          </SHStack>
        )}

        {isDisabled && (
          <SHStack
            width={"100%"}
            alignItems={"start"}
            justifyContent={"center"}
            paddingLeft={"13px"}
          >
            <SHTypography disabled>Not supported</SHTypography>
          </SHStack>
        )}
      </SHStack>

      {!isDisabled && (
        <>
          <SHStack
            width={"37%"}
            direction={"row"}
            justifyContent={"space-between"}
            gap={1}
          >
            <Controller
              control={control}
              name={fieldPaths.selectedPortfolioId}
              render={({ field: { ...others } }) => {
                return (
                  <SHAutocomplete
                    sx={{
                      "& .MuiAutocomplete-endAdornment": {
                        paddingRight: "0px",
                      },
                      "& .MuiAutocomplete-endAdornment .MuiAutocomplete-clearIndicator":
                        {
                          marginRight: "0px",
                          visibility: "visible",
                          svg: {
                            path: {
                              fill: palette.common.black,
                            },
                          },
                        },

                      "& .MuiAutocomplete-endAdornment .MuiAutocomplete-clearIndicator:hover":
                        {
                          svg: {
                            path: {
                              fill: palette.error[200],
                            },
                          },
                        },
                      visibility: isArchived ? "hidden" : "visible",
                    }}
                    fullWidth={true}
                    disabled={isArchived || isSectionDisabled}
                    options={portfolios ?? []}
                    getOptionLabel={(opt) => opt.label}
                    value={selectedPortfolio as SHFilterOption}
                    isOptionEqualToValue={(optA, optB) =>
                      optA?.value === optB?.value
                    }
                    textFieldProps={{
                      placeholder: !isArchived ? "Assign portfolio" : "",
                    }}
                    onChange={(_, opt) => {
                      others.onChange(opt?.value);
                      handleSelectedPortfolioChange(opt);
                    }}
                    renderOption={(props, option) => {
                      return (
                        <li
                          {...props}
                          key={option.value}
                          style={{ padding: "4px 10px" }}
                        >
                          <SHStack
                            alignItems={"center"}
                            direction="row"
                            spacing={1}
                            marginLeft={"-1px"}
                          >
                            <SHTypography variant="body3" fontWeight={500}>
                              {option.label}
                            </SHTypography>
                          </SHStack>
                        </li>
                      );
                    }}
                  />
                );
              }}
            />

            <SHStack
              height={"100%"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              {!isArchived ? (
                <DeleteButton
                  onClick={handleOnDelete}
                  tooltipTitle="Exclude from fee analysis. Note: Minimum one product from each platform must be included."
                  disabled={isLastRemainingSubProduct || isSectionDisabled}
                />
              ) : (
                <RollBackButton
                  onClick={handleOnRollBack}
                  height={"24px"}
                  width={"24px"}
                  tooltipTitle="Include in fee analysis."
                  disabled={isSectionDisabled}
                />
              )}
            </SHStack>
          </SHStack>
        </>
      )}
    </SHStack>
  );
};
